import { useCallback, useRef } from 'react';
import type { KuralGraphEntry, ThemeEntry } from '../data/types';
import type { Lang } from '../hooks/useLang';
import './GraphNodeList.css';

interface Props {
    focus: KuralGraphEntry;
    siblings: KuralGraphEntry[];
    related: { kural: KuralGraphEntry; shared_tags: string[] }[];
    themes: ThemeEntry[];
    lang: Lang;
    onNavigate: (target: { kind: 'kural' | 'theme'; id: number | string }) => void;
}

/**
 * Keyboard-accessible list of every node currently visible in the graph.
 * This is the primary path for screen-reader and keyboard-only users;
 * the SVG graph is a visual overlay of the same information.
 */
export default function GraphNodeList({
    focus,
    siblings,
    related,
    themes,
    lang,
    onNavigate,
}: Props) {
    const containerRef = useRef<HTMLDivElement>(null);

    // Arrow-key navigation across all buttons in the list.
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        const container = containerRef.current;
        if (!container) return;
        const buttons = Array.from(
            container.querySelectorAll<HTMLButtonElement>('button.graph-list__btn')
        );
        const activeIdx = buttons.findIndex((b) => b === document.activeElement);
        if (activeIdx < 0) return;
        e.preventDefault();
        const nextIdx =
            e.key === 'ArrowDown'
                ? Math.min(activeIdx + 1, buttons.length - 1)
                : Math.max(activeIdx - 1, 0);
        buttons[nextIdx]?.focus();
    }, []);

    return (
        <section
            className="graph-list"
            aria-label="Graph nodes"
            ref={containerRef}
            onKeyDown={handleKeyDown}
        >
            <h2 className="graph-list__heading">Graph nodes</h2>
            <p className="graph-list__hint">
                Arrow keys move between rows; Enter opens.
            </p>

            <div className="graph-list__group">
                <h3 className="graph-list__group-title">
                    Same chapter <span className="graph-list__count">({siblings.length})</span>
                </h3>
                <ul className="graph-list__ul">
                    <li>
                        <button
                            type="button"
                            className="graph-list__btn graph-list__btn--focus"
                            aria-current="true"
                            disabled
                        >
                            <span className="graph-list__n">Kural {focus.n}</span>
                            <span lang="ta" className="graph-list__ta">
                                {(focus.verse_ta && focus.verse_ta.length > 0
                                    ? focus.verse_ta
                                    : [focus.first_line_ta]
                                ).map((line, i) => (
                                    <span key={i} className="graph-list__ta-line">
                                        {line}
                                    </span>
                                ))}
                            </span>
                            <span className="graph-list__gist">{focus.gist_en}</span>
                            <span className="graph-list__here">focused</span>
                        </button>
                    </li>
                    {siblings.map((k) => (
                        <li key={k.n}>
                            <NodeRow
                                kural={k}
                                onActivate={() => onNavigate({ kind: 'kural', id: k.n })}
                            />
                        </li>
                    ))}
                </ul>
            </div>

            {related.length > 0 && (
                <div className="graph-list__group">
                    <h3 className="graph-list__group-title">
                        Shared themes{' '}
                        <span className="graph-list__count">({related.length})</span>
                    </h3>
                    <ul className="graph-list__ul">
                        {related.map(({ kural, shared_tags }) => (
                            <li key={kural.n}>
                                <NodeRow
                                    kural={kural}
                                    sharedTags={shared_tags}
                                    onActivate={() => onNavigate({ kind: 'kural', id: kural.n })}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {themes.length > 0 && (
                <div className="graph-list__group">
                    <h3 className="graph-list__group-title">
                        Themes <span className="graph-list__count">({themes.length})</span>
                    </h3>
                    <ul className="graph-list__ul">
                        {themes.map((t) => (
                            <li key={t.slug}>
                                <button
                                    type="button"
                                    className="graph-list__btn graph-list__btn--theme"
                                    onClick={() => onNavigate({ kind: 'theme', id: t.slug })}
                                    aria-label={`Open theme: ${t.title_en} (${t.title_ta})`}
                                >
                                    {lang === 'ta' ? (
                                        <>
                                            <span lang="ta" className="graph-list__theme-lead">
                                                {t.title_ta}
                                            </span>
                                            <span className="graph-list__theme-sub">{t.title_en}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="graph-list__theme-lead">{t.title_en}</span>
                                            <span lang="ta" className="graph-list__theme-sub">
                                                {t.title_ta}
                                            </span>
                                        </>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

interface NodeRowProps {
    kural: KuralGraphEntry;
    sharedTags?: string[];
    onActivate: () => void;
}

function NodeRow({ kural, sharedTags, onActivate }: NodeRowProps) {
    const aria = sharedTags
        ? `Open kural ${kural.n}: ${kural.first_line_ta}. ${kural.gist_en}. Shared themes: ${sharedTags.join(', ')}.`
        : `Open kural ${kural.n}: ${kural.first_line_ta}. ${kural.gist_en}.`;
    return (
        <button
            type="button"
            className="graph-list__btn"
            onClick={onActivate}
            aria-label={aria}
        >
            <span className="graph-list__n">Kural {kural.n}</span>
            <span lang="ta" className="graph-list__ta">
                {(kural.verse_ta && kural.verse_ta.length > 0
                    ? kural.verse_ta
                    : [kural.first_line_ta]
                ).map((line, i) => (
                    <span key={i} className="graph-list__ta-line">
                        {line}
                    </span>
                ))}
            </span>
            <span className="graph-list__gist">{kural.gist_en}</span>
            {sharedTags && sharedTags.length > 0 && (
                <span className="graph-list__shared" aria-hidden="true">
                    {sharedTags.join(' · ')}
                </span>
            )}
        </button>
    );
}
